import { supabase } from '../lib/supabase';

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

function toSnakeCase(obj) {
  const snakeObj = {};
  for (const key in obj) {
    const snakeKey = key?.replace(/([A-Z])/g, '_$1')?.toLowerCase();
    snakeObj[snakeKey] = obj?.[key];
  }
  return snakeObj;
}

function toCamelCase(obj) {
  if (!obj) return obj;
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  }
  return camelObj;
}

const subcontractService = {
  async getByProjectId(projectId) {
    try {
      const { data, error } = await supabase?.from('subcontracts')?.select(`
          *,
          subcontractors (
            id,
            company_name,
            trade,
            contact_person,
            contact_email,
            contact_phone
          ),
          project_costs (
            id,
            cost_code,
            description,
            category
          )
        `)?.eq('project_id', projectId)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else if (error?.code === 'PGRST116') {
          return [];
        } else {
          console.log('Data error:', error?.message);
          return [];
        }
      }

      return data?.map(subcontract => ({
        ...toCamelCase(subcontract),
        subcontractor: subcontract?.subcontractors ? toCamelCase(subcontract?.subcontractors) : null,
        projectCost: subcontract?.project_costs ? toCamelCase(subcontract?.project_costs) : null
      })) || [];
    } catch (error) {
      console.error('Error fetching subcontracts:', error);
      throw error;
    }
  },

  async create(subcontractData) {
    try {
      const snakeData = toSnakeCase(subcontractData);

      const insertPayload = {
        project_id: snakeData?.project_id,
        project_cost_id: snakeData?.project_cost_id,
        subcontractor_id: snakeData?.subcontractor_id,
        name: snakeData?.name,
        company: snakeData?.company,
        trade: snakeData?.trade,
        original_contract: snakeData?.original_contract,
        contract_value: snakeData?.contract_value,
        notes: snakeData?.notes ?? null,
        // Aligne sur le DEFAULT de la base, passe a 'draft' le 2026-08-30 :
        // un contrat qu'on vient de creer n'attend pas une signature.
        status: snakeData?.status || 'draft',
        document_type: snakeData?.document_type || 'contract',
        pricing_model: snakeData?.pricing_model || 'fixed'
      };

      console.log('[CREATE CONTRACT] insert payload:', insertPayload);

      const { data, error } = await supabase?.from('subcontracts')?.insert(insertPayload)?.select(`
          *,
          subcontractors (
            id,
            company_name,
            trade,
            contact_person,
            contact_email,
            contact_phone
          ),
          project_costs (
            id,
            cost_code,
            description,
            category
          )
        `)?.single();

      if (error) {
        console.error('[CREATE CONTRACT] supabase error:', error);
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          alert(error?.message);
          return null;
        }
      }

      return {
        ...toCamelCase(data),
        subcontractor: data?.subcontractors ? toCamelCase(data?.subcontractors) : null,
        projectCost: data?.project_costs ? toCamelCase(data?.project_costs) : null
      };
    } catch (error) {
      console.error('Error creating subcontract:', error);
      throw error;
    }
  },

  async update(id, subcontractData) {
    try {
      const snakeData = toSnakeCase(subcontractData);
      const { data, error } = await supabase?.from('subcontracts')?.update(snakeData)?.eq('id', id)?.select(`
          *,
          subcontractors (
            id,
            company_name,
            trade,
            contact_person,
            contact_email,
            contact_phone
          ),
          project_costs (
            id,
            cost_code,
            description,
            category
          )
        `)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return null;
        }
      }

      return {
        ...toCamelCase(data),
        subcontractor: data?.subcontractors ? toCamelCase(data?.subcontractors) : null,
        projectCost: data?.project_costs ? toCamelCase(data?.project_costs) : null
      };
    } catch (error) {
      console.error('Error updating subcontract:', error);
      throw error;
    }
  },

  /**
   * Recense tout ce qui est rattache a un contrat, avant suppression.
   * Retourne { total, avenants, subInvoices, subInvoiceLines, extras, extraLines, corrections }.
   * Requetes de comptage uniquement (head: true), donc soumises au RLS courant :
   * ce que l'utilisateur n'a pas le droit de voir n'est pas compte.
   */
  async getDeleteBlockers(subcontractId) {
    const blockers = {
      avenants: 0,
      subInvoices: 0,
      subInvoiceLines: 0,
      extras: 0,
      extraLines: 0,
      corrections: 0,
      quittances: 0,
      total: 0,
    };
    if (!subcontractId) return blockers;

    const countBy = async (table, column, value) => {
      const { count, error } = await supabase
        ?.from(table)
        ?.select('id', { count: 'exact', head: true })
        ?.eq(column, value);
      if (error) throw error;
      return count || 0;
    };

    // Variante tolerante : une table pas encore creee ne doit pas faire
    // echouer tout le controle. On compte 0 et on continue.
    const countByOptional = async (table, column, value) => {
      try {
        return await countBy(table, column, value);
      } catch (err) {
        console.error(`getDeleteBlockers: ${table} unavailable`, err?.message);
        return 0;
      }
    };

    // Les factures sous-traitants portent deux colonnes historiques vers le contrat.
    const countSubInvoices = async () => {
      const { count, error } = await supabase
        ?.from('sub_invoices')
        ?.select('id', { count: 'exact', head: true })
        ?.or(`subcontract_id.eq.${subcontractId},contract_id.eq.${subcontractId}`);
      if (error) throw error;
      return count || 0;
    };

    const [avenants, subInvoices, subInvoiceLines, extras, extraLines, corrections, quittances] =
      await Promise.all([
        countBy('subcontract_change_orders', 'subcontract_id', subcontractId),
        countSubInvoices(),
        countBy('sub_invoice_lines', 'subcontract_id', subcontractId),
        countBy('project_extras', 'subcontract_id', subcontractId),
        countBy('project_extra_lines', 'subcontract_id', subcontractId),
        countBy('cost_code_corrections', 'subcontract_id', subcontractId),
        // quittances.subcontract_id est en ON DELETE RESTRICT : sans ce
        // comptage, la base refuserait la suppression sans explication.
        countByOptional('quittances', 'subcontract_id', subcontractId),
      ]);

    blockers.avenants = avenants;
    blockers.subInvoices = subInvoices;
    blockers.subInvoiceLines = subInvoiceLines;
    blockers.extras = extras;
    blockers.extraLines = extraLines;
    blockers.corrections = corrections;
    blockers.quittances = quittances;
    blockers.total =
      avenants + subInvoices + subInvoiceLines + extras + extraLines + corrections + quittances;

    return blockers;
  },

  async delete(id) {
    try {
      const { error } = await supabase?.from('subcontracts')?.delete()?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        } else {
          console.log('Data error:', error?.message);
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Error deleting subcontract:', error);
      throw error;
    }
  }
};

export default subcontractService;